<template>
    <section class="flex flex-col gap-6">
        <header>
            <AssessmentEyebrow>{{ t("components.assessment.beforeYouBegin") }}</AssessmentEyebrow>
            <h1 class="display-serif text-ink mt-3">{{ heading }}</h1>
            <p v-if="meta" class="scaffold-details-small text-ink-muted mt-2">{{ meta }}</p>
        </header>

        <MarkdownText v-if="intro.purpose" :text="intro.purpose" class="body-small text-ink-muted" />

        <div v-if="cards.length" class="grid grid-cols-1 gap-3" :class="cardGridClass">
            <BaseCard v-for="(card, index) in cards" :key="index" padding="sm">
                <div class="body-small-bold text-ink">{{ card.title }}</div>
                <MarkdownText v-if="card.body" :text="card.body" class="scaffold-details-small text-ink-muted mt-1" />
            </BaseCard>
        </div>

        <footer v-if="hasFooter" class="rounded-lg bg-surface-sunken px-4 py-3 text-ink-muted" data-testid="assessment-intro-footer">
            <div v-if="intro.footer_title" class="body-small-bold">{{ intro.footer_title }}</div>
            <MarkdownText v-if="intro.footer_body" :text="intro.footer_body" class="scaffold-details-small" :class="{ 'mt-1': intro.footer_title }" />
        </footer>

        <div class="flex justify-end">
            <BaseButton theme="primary" @click="emit('begin')">{{ t("components.assessment.begin") }} <i class="bi bi-arrow-right" aria-hidden="true"></i></BaseButton>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { AssessmentIntro } from "~vue/api/generated/reflections";
import AssessmentEyebrow from "~vue/components/AssessmentEyebrow.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
    intro: AssessmentIntro;
}

const props = defineProps<Props>();
const emit = defineEmits<{ begin: [] }>();

const heading = computed(() => props.intro.heading || t("components.assessment.welcome"));
const cards = computed(() => props.intro.cards ?? []);
const hasFooter = computed(() => Boolean(props.intro.footer_title || props.intro.footer_body));
const cardGridClass = computed(() => (cards.value.length >= 3 ? "sm:grid-cols-3" : cards.value.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-1"));

const meta = computed(() => {
    const parts: string[] = [];
    const questionCount = props.intro.question_count ?? 0;
    if (questionCount > 0) {
        parts.push(`${questionCount} ${questionCount === 1 ? "question" : "questions"}`);
    }
    const minutes = props.intro.estimated_minutes ?? 0;
    if (minutes > 0) {
        parts.push(`about ${minutes} min`);
    }
    return parts.join(" · ");
});
</script>
